import * as React from 'react';
import {
    CreateBase,
    useNotify,
    useRefresh,
    useDataProvider,
    useEditContext,
    EditBase,
} from 'react-admin';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton as MuiIconButton,
    Box,
    Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { TextField as MuiTextField, MenuItem } from '@mui/material';
import { CommissionService } from "../../services/commission/commissionService.ts";
import { COMMISSIONS } from '../../enums/Commissions.ts';
import * as Sentry from '@sentry/react';

type Role = keyof typeof COMMISSIONS;

const COMMISSION_CHOICES = (Object.keys(COMMISSIONS) as Role[]).map((id) => ({
    id,
    name: COMMISSIONS[id].label,
}));

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-secondary)' },
        '& .MuiSelect-outlined': {
            borderRadius: '6px',
            height: '1.4375em',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-secondary)' },
        },
    },
    '& .MuiInputBase-input': { fontSize: 14, padding: '10px 12px' },
};

type Props = { open: boolean; onClose: () => void };

export const CommissionEditForm: React.FC<Props> = ({ open, onClose }) => {
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const refresh = useRefresh();
    const [items, setItems] = React.useState<
        { id: string; label: string; amount: number | string; unitType: string; commissionId?: string | null }[]
    >([]);
    const { record, isLoading } = useEditContext<any>();
    console.log(record);

    const getKeyByLabel = (label: string): Role | undefined =>
        (Object.keys(COMMISSIONS) as Role[]).find(k => COMMISSIONS[k].label === label);

    const getUnitByLabel = (label: string): string =>
        Object.values(COMMISSIONS).find(c => c.label === label)?.unit ?? '';


    React.useEffect(() => {
        if (!record?.agencyCommissionList) return;
        const list = record.agencyCommissionList.map(
            (row: { id?: string; amount: number; commissionType: string }) => {
                const key = getKeyByLabel(row.commissionType) ?? (row.commissionType as Role);
                return {
                    id: key,
                    label: row.commissionType,
                    amount: row.amount,
                    unitType: getUnitByLabel(row.commissionType),
                    commissionId: row.id ?? null,
                };
            }
        );
        setItems(list);
    }, [record]);


    const initialValues = {
        name: record?.agencyName ?? '',
        role: '',
        amount: '',
        unitType: '',
        editingCommissionId: null as string | null,
    };

    const validationSchema = (items: Array<{ id: string }>) =>
        Yup.object({
            name: Yup.string().required('Required'),
            role: Yup.string().required('Required')
                .test('unique', 'This commission has already been added', (role) => !items.some(it => it.id === role)),
            amount: Yup.number().typeError('Must be a number').required('Required'),
            unitType: Yup.string().required('Required'),
        });

    const { t } = useTranslation();
    const commissionService = new CommissionService();

    return (
        <CreateBase resource="commissionManagement">
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="lg"
                slotProps={{
                    backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.5)' } },
                    paper: { sx: { borderRadius: 2, border: '1px solid var(--color-secondary)' } },
                }}
                sx={{ padding: 0 }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {t('commissionManagement.editAgency')}
                    <MuiIconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </MuiIconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <Formik
                        initialValues={initialValues}
                        enableReinitialize
                        validationSchema={validationSchema(items)}
                        onSubmit={async (values, { resetForm, setSubmitting }) => {
                            const role = values.role as Role;
                            setItems(prev => [
                                ...prev,
                                {
                                    id: role,
                                    label: COMMISSIONS[role].label,
                                    amount: values.amount,
                                    unitType: COMMISSIONS[role].unit,
                                    commissionId: values.editingCommissionId,
                                },
                            ]);
                            notify('Item added', { type: 'success' });
                            resetForm({ values: { ...values, role: '', amount: '', unitType: '' } });
                            setSubmitting(false);
                        }}

                    >
                        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
                            <Form onSubmit={handleSubmit}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, width: '100%' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '50%', gap: 3 }}>
                                        <MuiTextField
                                            name="name"
                                            label=""
                                            placeholder={t('commissionManagement.dialog.nameOfAgency')}
                                            sx={inputSx}
                                            size="small"
                                            fullWidth
                                            value={values.name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.name && Boolean(errors.name)}
                                        // helperText={touched.name && errors.name}
                                        />

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                borderColor: 'var(--color-secondary)',
                                                gap: 3,
                                                borderWidth: 1,
                                                borderStyle: 'solid',
                                                padding: 3,
                                                borderRadius: 'var(--radius-6)',
                                            }}
                                        >
                                            <MuiTextField
                                                select
                                                disabled
                                                name="role"
                                                label=""
                                                sx={inputSx}
                                                size="small"
                                                fullWidth
                                                value={values.role}
                                                onChange={(e) => {
                                                    const role = e.target.value as Role;
                                                    setFieldValue('role', role);
                                                    setFieldValue('unitType', role ? COMMISSIONS[role].unit : '');
                                                }}
                                                onBlur={handleBlur}
                                                error={touched.role && Boolean(errors.role)}
                                                helperText={touched.role && errors.role}
                                            >
                                                {COMMISSION_CHOICES.map(c => (
                                                    <MenuItem key={c.id} value={c.id}>
                                                        {c.name}
                                                    </MenuItem>
                                                ))}
                                            </MuiTextField>

                                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                                                <MuiTextField
                                                    name="amount"
                                                    label=""
                                                    disabled={values.role == ''}
                                                    placeholder={t('commissionManagement.dialog.amount')}
                                                    sx={inputSx}
                                                    size="small"
                                                    fullWidth
                                                    value={values.amount}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.amount && Boolean(errors.amount)}
                                                    helperText={touched.amount && errors.amount}
                                                />

                                                <MuiTextField
                                                    name="unitType"
                                                    label=""
                                                    sx={inputSx}
                                                    size="small"
                                                    fullWidth
                                                    value={values.unitType}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    disabled
                                                    error={touched.role && Boolean(errors.role)}
                                                    helperText={touched.role && errors.role}
                                                >
                                                    {/* {COMMISSION_UNIT_TYPES.map(c => (
                            <MenuItem key={c.id} value={c.name}>
                            {c.name}
                            </MenuItem>
                          ))} */}
                                                </MuiTextField>
                                            </Box>

                                            <Button
                                                type="submit"
                                                variant="contained"
                                                disabled={values.amount == '' || values.role == ''}
                                                sx={{
                                                    width: 'fit-content',
                                                    px: 4,
                                                    borderRadius: 0,
                                                    backgroundColor: '#18C5B3',
                                                    color: 'white',
                                                    boxShadow: 'none',
                                                    alignSelf: 'flex-end',
                                                    '&:hover': { cursor: 'pointer', backgroundColor: 'var(--color-primary-hover)', boxShadow: 'var(--shadow-1)' },
                                                    '&:disabled': { cursor: 'default', backgroundColor: 'var(--color-button-background-disabled)', color: 'var(--color-button-text-disabled)' }
                                                }}
                                            >
                                                {t('common.change')}
                                            </Button>
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            width: '50%',
                                            borderColor: 'var(--color-secondary)',
                                            borderWidth: 1,
                                            borderStyle: 'solid',
                                            padding: 3,
                                            borderRadius: 'var(--radius-6)',
                                            gap: 1.5,
                                        }}
                                    >
                                        {items.map((it, idx) => (
                                            <Box
                                                key={`${it.id}-${idx}`}
                                                sx={{
                                                    transition: 'background-color 200ms ease, box-shadow 200ms ease',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    border: '1px solid var(--color-secondary)',
                                                    borderRadius: '6px',
                                                    px: 2,
                                                    py: 1,
                                                    '&:hover': { cursor: 'pointer', backgroundColor: 'var(--color-secondary)', boxShadow: 'var(--shadow-1)' },
                                                }}
                                                onClick={() => {
                                                    setFieldValue('role', it.id);
                                                    setFieldValue('amount', String(it.amount));
                                                    setFieldValue('unitType', COMMISSIONS[it.id as Role]?.unit ?? it.unitType);
                                                    setFieldValue('editingCommissionId', it.commissionId ?? null);

                                                    setItems(prev => prev.filter((_, i) => i !== idx));
                                                }}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <Box sx={{ flex: 1 }}>{it.label}</Box>
                                                <Box sx={{ width: 80, textAlign: 'right' }}>{it.amount}</Box>
                                                <Box sx={{ width: 80, textAlign: 'right' }}>{it.unitType}</Box>
                                                <Button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setItems(prev => prev.filter((_, i) => i !== idx))
                                                    }}
                                                    variant="text"
                                                    sx={{ minWidth: 0 }}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </Button>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                                    <Button
                                        variant="outlined"
                                        type="button"
                                        onClick={onClose}
                                        sx={{ borderRadius: 0, borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
                                    >
                                        {t('common.cancel')}
                                    </Button>

                                    <Button
                                        type="button"
                                        onClick={async () => {
                                            try {
                                                const payload = {
                                                    id: record?.id,
                                                    agencyName: values.name.trim(),
                                                    agencyCommissionList: items.map(it => ({
                                                        id: it.commissionId,
                                                        amount: it.amount,
                                                        commissionType: it.label
                                                    })),
                                                };
                                                console.log(payload);
                                                await commissionService.updateAgency(payload);

                                                notify(t('common.saved'), { type: 'success' });
                                                setItems([]);
                                                refresh();
                                                onClose();
                                            } catch (error: any) {
                                                notify(error.response.data.error, { type: 'error' });
                                                console.error(error.response.data.error);
                                                Sentry.captureException(error);
                                            }
                                        }}
                                        disabled={items.length === 0}
                                        sx={{
                                            borderRadius: 0,
                                            backgroundColor: 'var(--color-primary)',
                                            color: 'var(--color-text-light)',
                                            boxShadow: 'none',
                                            '&:hover': { cursor: 'pointer', backgroundColor: 'var(--color-primary-hover)', boxShadow: 'var(--shadow-1)' },
                                            '&:disabled': { cursor: 'default', backgroundColor: 'var(--color-button-background-disabled)', color: 'var(--color-button-text-disabled)' }
                                        }}
                                    >
                                        {t('common.saveChanges')}
                                    </Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
        </CreateBase>
    );
};

export const CommissionEditDialog: React.FC = ({ id, open, onClose }) => (
    <EditBase title="Commission Management" actions={false} id={id} queryOptions={{ enabled: false }}>
        <CommissionEditForm open={open} onClose={onClose} />
    </EditBase>
);